/**
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { Box } from '@mui/material';
import { isNil } from 'lodash';
import { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { FlightLegInfo } from '../../../../shared/entities/flightLeg';
import {
  selectFlightLegsInfo,
  selectIsPerformanceOutlookLoading,
  selectMissionPlanById,
  selectOverlaysCurrentContext,
  selectSelectedMissionPlanId,
  selectUserId,
} from '../../../../store';
import { switchOverlaysContext } from '../../../../store/actions';
import { requestBeams, requestFlHeatMaps, requestRlHeatMaps } from '../../../../store/beams/actions';
import { getFlightLegsInfo } from '../../../../store/flightLegs/actions';
import { FLEET_MAP_ID, setMapHidden } from '../../../../store/maps/actions';
import { OverlaysContextsEnum } from '../../../../store/maps/reducer';
import { getMissionPlanById } from '../../../../store/missionPlans/actions';
import { clearExportViewTitle, setPrintMode, unsetPrintMode } from '../../../../store/ui/actions';
import ModalLoadingSpinner from '../../../common/layout/ModalLoadingSpinner';
import MissionPlanNotLoadedModal from '../MissionPlanNotLoadedModal';
import { ContentDisplayEnum, ContourDisplayEnum } from '../PerformanceOutlookExportModal';
import PerfomanceOutlookCompactPrint from './PerfomanceOutlookCompactPrint';
import PerfomanceOutlookStandardPrint from './PerfomanceOutlookStandardPrint';
import PerformanceOutlookExportDisclaimer from './PerformanceOutlookExportDisclaimer';
import PerformanceOutlookExportHeader from './PerformanceOutlookExportHeader';
import {getArcLightMapVersion, getViHnaMapVersion} from "../../../../utils/BeamUtils";
type ParsedUrlQuery = {
  contentDensity: ContentDisplayEnum;
  currentLeg?: string;
  contourDisplay: ContourDisplayEnum;
};

const PerformanceOutlookExportContainer = () => {
  const missionPlanId = useSelector(selectSelectedMissionPlanId, shallowEqual);
  const flightLegs: FlightLegInfo[] = useSelector(selectFlightLegsInfo, shallowEqual);
  const userId = useSelector(selectUserId, shallowEqual);
  const missionPlan = useSelector(selectMissionPlanById, shallowEqual);
  const isLoading = useSelector(selectIsPerformanceOutlookLoading, shallowEqual);

  const dispatch = useDispatch();

  const overlaysCurrentContext = useSelector(selectOverlaysCurrentContext, shallowEqual);

  const [missionPlanLoadError, setMissionPlanLoadError] = useState(false);

  const { search } = useLocation();
  const [queryParams, setQueryParams] = useState<ParsedUrlQuery | undefined>(
    search ? (Object.fromEntries(new URLSearchParams(search)) as ParsedUrlQuery) : undefined,
  );
  const [contourDisplay, setContourDisplay] = useState(
    queryParams && Object.values(ContourDisplayEnum).includes(queryParams.contourDisplay)
      ? queryParams.contourDisplay
      : ContourDisplayEnum.DEFAULT,
  );

  useEffect(() => {
    return () => {
      dispatch(clearExportViewTitle());
    };
  }, [dispatch]);

  useEffect(() => {
    setQueryParams(search ? (Object.fromEntries(new URLSearchParams(search)) as ParsedUrlQuery) : undefined);
  }, [search]);

  useEffect(() => {
    // Contour to display on maps (defaults to showing no contours)
    setContourDisplay(
      queryParams && Object.values(ContourDisplayEnum).includes(queryParams.contourDisplay)
        ? queryParams.contourDisplay
        : ContourDisplayEnum.DEFAULT,
    );
  }, [queryParams]);

  useEffect(() => {
    if (contourDisplay === ContourDisplayEnum.FL || contourDisplay === ContourDisplayEnum.RL) {
      if (missionPlan.antennaTypeId !== -1 && !isNil(missionPlan.sscfFileVersion)) {
        dispatch(requestFlHeatMaps(missionPlan.antennaTypeId, missionPlan.sscfFileVersion));
        dispatch(requestRlHeatMaps(missionPlan.antennaTypeId, missionPlan.sscfFileVersion));
      }
    }
  }, [contourDisplay, missionPlan.antennaTypeId, missionPlan.sedFileVersion, missionPlan.sscfFileVersion, dispatch]);

  // Export density (defaults to standard)
  const density =
    queryParams && Object.values(ContentDisplayEnum).includes(queryParams.contentDensity)
      ? queryParams.contentDensity
      : ContentDisplayEnum.STANDARD;

  // Current Leg to show on page (defaults to undefined if currentLeg is undefined, not a number or negative)
  // Display all legs if currentLeg is undefined
  const currentLeg =
    queryParams?.currentLeg && !isNaN(Number(queryParams.currentLeg)) && Number(queryParams.currentLeg) > -1
      ? flightLegs.find(leg => leg.id === Number(queryParams.currentLeg))
      : undefined;

  useEffect(() => {
    dispatch(switchOverlaysContext(OverlaysContextsEnum.PERFORMANCE_OUTLOOK_EXPORT));
    // hide the fleet/FTV view map in the case of refresh on any MPT page
    dispatch(setMapHidden(FLEET_MAP_ID)(true));
  }, [dispatch]);

  useEffect(() => {
    if (missionPlanId !== undefined && missionPlan.id !== missionPlanId && userId) {
      dispatch(getMissionPlanById(userId, missionPlanId));
    }
  }, [dispatch, missionPlanId, missionPlan, userId]);

  useEffect(() => {
    if (missionPlan.id && userId) {
      dispatch(getFlightLegsInfo(missionPlan.id, userId));
      const arcLightMapVersion = getArcLightMapVersion(missionPlan.antennaTypeId, missionPlan.sscfFileVersion);
      const viHnaMapVersion = getViHnaMapVersion(missionPlan.viHnaPolicyVersion);
      dispatch(
        requestBeams(arcLightMapVersion, viHnaMapVersion),
      );
    }
  }, [dispatch, missionPlan, userId]);

  useEffect(() => {
    if (missionPlanId === undefined) {
      setMissionPlanLoadError(true);
    }
  }, [missionPlanId]);

  useEffect(() => {
    dispatch(setPrintMode());
    return () => {
      dispatch(unsetPrintMode());
    };
  }, [dispatch]);
  return userId && overlaysCurrentContext === OverlaysContextsEnum.PERFORMANCE_OUTLOOK_EXPORT ? (
    <Box>
      <PerformanceOutlookExportHeader />

      {!isNil(currentLeg) ? (
        density === ContentDisplayEnum.COMPACT ? (
          <PerfomanceOutlookCompactPrint
            key={currentLeg.id}
            flightLeg={currentLeg}
            missionPlan={missionPlan}
            userId={userId}
            totalFlightLegs={1}
            contourDisplayType={contourDisplay}
          />
        ) : (
          <PerfomanceOutlookStandardPrint
            key={currentLeg.id}
            flightLeg={currentLeg}
            missionPlan={missionPlan}
            userId={userId}
            totalFlightLegs={flightLegs.length}
            contourDisplayType={contourDisplay}
          />
        )
      ) : (
        (flightLegs || []).map((flightLeg: FlightLegInfo, index: number) => (
          <Box
            position="relative"
            sx={{
              '@media print': {
                color: 'black',
                backgroundColor: 'white',
                '@page': {
                  size: 'letter landscape',
                  margin: '10mm 10mm 10mm 10mm' /* this affects the margin in the printer settings */,
                },

                html: {
                  backgroundColor: '#FFFFFF',
                  margin: 0 /* this affects the margin on the html before sending to printer */,
                },

                body: {
                  overflow: 'visible !important',
                  margin: '0 !important' /* margin you want for the content */,
                },
              },
            }}
          >
            {density === ContentDisplayEnum.COMPACT ? (
              <Box
                sx={{
                  width: '100%',
                  display: 'block',
                }}
              >
                <PerfomanceOutlookCompactPrint
                  key={flightLeg.id}
                  flightLeg={flightLeg}
                  missionPlan={missionPlan}
                  userId={userId}
                  totalFlightLegs={flightLegs.length}
                  contourDisplayType={contourDisplay}
                />
              </Box>
            ) : (
              <PerfomanceOutlookStandardPrint
                key={flightLeg.id}
                flightLeg={flightLeg}
                missionPlan={missionPlan}
                userId={userId}
                totalFlightLegs={flightLegs.length}
                contourDisplayType={contourDisplay}
              />
            )}
          </Box>
        ))
      )}

      <PerformanceOutlookExportDisclaimer />
      <MissionPlanNotLoadedModal
        open={missionPlanLoadError}
        onClose={() => {
          setMissionPlanLoadError(false);
        }}
      />
      {isLoading && <ModalLoadingSpinner />}
    </Box>
  ) : (
    <></>
  );
};

export default PerformanceOutlookExportContainer;
